/**
 * บอสโลก (docs/systems/WORLD_BOSSES.md, data/worldBosses.ts): a boss lands every hour on `world.time` (ape, bird, turtle in turn),
 * is announced 10 and 2 minutes ahead, stays 15 minutes and leaves if nobody kills it. No dice in the schedule.
 *
 * - Health is 4,000 × max(5, n) for the attendance n (simulated helpers 0.5 each) and only grows in a fight.
 * - Moves are `scripted` skills: the World winds them up, `bossMoves.ts` fixes the ground shape (the telegraph, sent as
 *   the dungeon's `dungeonTele` events) and this system strikes everyone standing in it when the wind-up ends.
 * - Contribution points (damage, guarding, mechanics answered, presence) rank everyone who earned 40+; the top 10 %
 *   are gold, the next 30 % silver, the rest copper. Chests are paid to the real player only, twice a day per boss.
 * - Leash: the arena circle; nobody inside for 15 s and the boss goes home and heals. ความคลั่ง at 8 minutes.
 * - Mode `worldBoss` (data/modes.ts): in `npc` simulated players walk in and fight (and take no loot); `pvp` has none.
 *
 * Randomness only through the host's `random`, time only from `world.time` and `dt`. `state()` is what the renderer reads.
 */
import {
  WB_CONTRIB,
  WB_CYCLE,
  WB_DAILY,
  WB_ENRAGE,
  WB_LEASH,
  WB_MOVES,
  WB_PHASES,
  WB_SCALE,
  WB_SIM,
  WORLD_BOSS,
  WB_HOME,
  WB_PARTY,
  WB_SPOT_R,
  splitRewards,
  tierOf,
  bossAt,
  bossHp,
  contribution,
  newWorldBossSave,
  nextBoss,
  simName,
  slotStart,
  type BossMove,
  type BossMoveRule,
  type WbTier,
  type WorldBossDef,
  type WorldBossId,
} from '../data/worldBosses';
import { BAG_SIZE, ITEMS } from '../data/items';
import { getSkill } from '../data/skills';
import { inSafeArea, isSolid } from '../map';
import { dist, MOVEMENT, type Entity, type WorldEvent } from '../world';
import { inShape, shapeOf, type BossShape } from './bossMoves';
import { BossMech, type MechView, type StrikeTele } from './worldBossMech';
import { modeNow } from './modes';
import type { SystemHost, WorldSystem } from './types';

import type { QgDropEvent } from './qinggongDrops';

export type WorldBossEvent =
  /** A qinggong page, manual or seal was paid (systems/qinggongDrops.ts). */
  | QgDropEvent
  /** The boss's story: `soon` (n seconds ahead), `start`, `phase` (n = 2 or 3), `enrage`, `leash` (n = 1 going home, 0 back), `end`. */
  | { type: 'worldBoss'; id: WorldBossId; state: 'soon' | 'start' | 'phase' | 'enrage' | 'leash' | 'end'; n: number; win: boolean }
  /** What the real player was paid. `tier` is 'none' when under 40 points. */
  | {
      type: 'worldBossLoot';
      id: WorldBossId;
      tier: WbTier | 'none';
      rank: number;
      of: number;
      points: number;
      full: boolean;
      gold: number;
      cultivation: number;
      items: string[];
      rare: string[];
    };

interface Part {
  readonly id: number;
  readonly sim: boolean;
  readonly name: string;
  dmg: number;
  guard: number;
  heal: number;
  mech: number;
  present: number;
  lastAct: number;
  /** J3: the player used auto at this fight: paid the helper tier only. */
  auto?: boolean;
}

type Tele = StrikeTele;

interface Fight {
  readonly k: number;
  readonly def: WorldBossDef;
  readonly bossId: number;
  readonly landed: number;
  readonly mode: 'npc' | 'pvp';
  readonly parts: Map<number, Part>;
  readonly teles: Tele[];
  readonly baseAtk: number;
  phase: 1 | 2 | 3;
  beatT: number;
  beatHp: number;
  /** Seconds since the first hit; null before it. */
  fightT: number | null;
  /** The entity that last damaged the boss (the killing blow when it dies). */
  lastHit: number;
  enraged: boolean;
  bigT: number;
  sampleT: number;
  idleT: number;
  resetting: boolean;
  decisionT: number;
  buffT: number;
  sims: number;
  simSeq: number;
  /** Set when it ended: win, and seconds the body stays. */
  over: { win: boolean; t: number } | null;
}

/** What the renderer and HUD read. */
export interface WorldBossState {
  readonly id: WorldBossId;
  readonly entityId: number;
  readonly phase: 1 | 2 | 3;
  readonly hp: number;
  readonly maxHp: number;
  /** Seconds until ความคลั่ง (null before the first hit), and whether it is on. */
  readonly enrageIn: number | null;
  readonly enraged: boolean;
  readonly resetting: boolean;
  readonly staged: boolean;
  /** Seconds before the boss leaves. */
  readonly left: number;
  readonly arena: { readonly x: number; readonly y: number; readonly r: number };
  readonly attendance: number;
  readonly sims: number;
  readonly mode: 'npc' | 'pvp';
  readonly points: number;
  /** The player's place among those with 40+ points now (0 first), how many such there are, and the chest tier that place pays ('none' under 40). */
  readonly rank: number;
  readonly of: number;
  readonly tier: WbTier | 'none';
  readonly over: boolean;
  /** The tricks in play: the ape's ledge and stance, the bird aloft and its eggs, the turtle's shell, patches, marks (round 2). */
  readonly mech: MechView | null;
}

const SYSTEMS = new WeakMap<object, WorldBossesSystem>();
const DECISION = 1.2;

export class WorldBossesSystem implements WorldSystem {
  readonly name = 'worldBosses';
  fight: Fight | null = null;
  private warned = new Set<string>();
  private done = new Set<number>();
  private teleIds = 1;
  /** The mechanics of the fight in progress (systems/worldBossMech.ts). */
  mech: BossMech | null = null;

  constructor(private readonly h: SystemHost) {
    SYSTEMS.set(h.world, this);
  }

  private get w() {
    return this.h.world;
  }

  private send(id: WorldBossId, state: 'soon' | 'start' | 'phase' | 'enrage' | 'leash' | 'end', n = 0, win = false): void {
    this.h.emit({ type: 'worldBoss', id, state, n, win });
  }

  private here(id: WorldBossId): boolean {
    const w = this.w;
    return w.map.zone === WB_HOME[id] && !w.player.dead && !w.interiors.inside;
  }

  // ───────────────────────── read side ─────────────────────────

  /** The fight in progress (or just over), for the HUD and the renderer; null when no boss is up. */
  state(): WorldBossState | null {
    const f = this.fight;
    const e = f && this.w.entities.get(f.bossId);
    if (!f || !e) return null;
    const mine = f.parts.get(this.w.player.id);
    const pts = (p: Part) => this.points(f, p);
    const ranked = [...f.parts.values()].filter((p) => pts(p) >= WB_CONTRIB.min).sort((a, b) => pts(b) - pts(a) || b.id - a.id);
    const rank = mine ? ranked.indexOf(mine) : -1;
    return {
      id: f.def.id,
      entityId: f.bossId,
      phase: f.phase,
      hp: e.hp,
      maxHp: e.maxHp,
      enrageIn: f.fightT == null ? null : Math.max(0, WB_ENRAGE.at - f.fightT),
      enraged: f.enraged,
      resetting: f.resetting,
      staged: f.beatT > 0,
      left: Math.max(0, slotStart(f.k) + WB_CYCLE.life - this.w.time),
      arena: f.def.arena,
      attendance: this.attendance(f),
      sims: f.sims,
      mode: f.mode,
      points: mine ? this.points(f, mine) : 0,
      rank,
      of: ranked.length,
      tier: rank < 0 ? 'none' : tierOf(rank, ranked.length),
      over: !!f.over,
      mech: this.mech?.view() ?? null,
    };
  }

  /** Points of one fighter now. */
  points(f: Fight, p: Part): number {
    const e = this.w.entities.get(f.bossId);
    return contribution({ ...p, maxHp: e?.maxHp ?? 1 });
  }

  /** People-equivalents in the arena: players who acted in the last 20 s, plus the helpers at half. */
  private attendance(f: Fight): number {
    let n = f.sims * WB_SCALE.helperWeight;
    for (const p of f.parts.values())
      if (!p.sim && this.w.time - p.lastAct <= WB_SCALE.active) n++;
    return n;
  }

  /** A player (entity id) answered one of the boss's mechanics (R2 hooks: a nest egg put out, a rod rung ...). */
  creditMechanic(playerId: number): void {
    const f = this.fight;
    if (!f || f.over) return;
    const p = this.human(f, playerId);
    if (p) {
      p.mech++;
      p.lastAct = this.w.time;
    }
  }

  /** A player healed someone fighting the boss (`amount` health). */
  creditHeal(playerId: number, amount: number): void {
    const f = this.fight;
    const p = f && !f.over ? this.human(f, playerId) : null;
    if (p) {
      p.heal += amount;
      p.lastAct = this.w.time;
    }
  }

  private human(f: Fight, id: number): Part | null {
    const e = this.w.entities.get(id);
    if (!e || e.kind !== 'player') return null;
    let p = f.parts.get(id);
    if (!p) {
      p = { id, sim: false, name: e.name, dmg: 0, guard: 0, heal: 0, mech: 0, present: 0, lastAct: -1e9 };
      f.parts.set(id, p);
    }
    if (id === this.w.player.id && this.w.auto.on) p.auto = true;
    return p;
  }

  private addSims(f: Fight, n: number): void {
    for (let i = 0; i < n; i++) {
      const id = -1 - f.simSeq;
      f.parts.set(id, { id, sim: true, name: simName(f.simSeq), dmg: 0, guard: 0, heal: 0, mech: 0, present: 0, lastAct: -1e9 });
      f.simSeq++;
      f.sims++;
    }
  }

  // ───────────────────────── schedule ─────────────────────────

  step(dt: number): void {
    const w = this.w;
    const f = this.fight;
    if (f) return this.stepFight(f, dt);
    const nx = nextBoss(w.time);
    const due = this.here(nx.id) ? WB_CYCLE.warn.filter((s) => nx.in <= s && !this.warned.has(`${nx.k}:${s}`)) : [];
    if (due.length) {
      for (const s of due) this.warned.add(`${nx.k}:${s}`);
      const name = WORLD_BOSS[nx.id]!.name;
      this.send(nx.id, 'soon', Math.ceil(nx.in));
      this.h.notice(`อีก ${Math.ceil(nx.in / 60)} นาที ${name}จะมา`);
    }
    const up = bossAt(w.time);
    if (up && this.here(up.id) && !this.done.has(up.k)) this.start(up.k, up.id);
  }

  /**
   * A random walkable spot on this map for the boss and its arena, never in the safe plaza (the spot and 8 points of the
   * arena ring are checked). Null when no spot is found in 200 tries: the slot passes.
   */
  private placeOf(def: WorldBossDef): { arena: WorldBossDef['arena']; spawn: { x: number; y: number } } | null {
    const m = this.w.map;
    const r = Math.min(def.arena.r, WB_SPOT_R);
    for (let n = 0; n < 200; n++) {
      const x = Math.floor(r + 2 + this.h.random() * (m.w - 2 * r - 4)) + 0.5;
      const y = Math.floor(r + 2 + this.h.random() * (m.h - 2 * r - 4)) + 0.5;
      let ok = !isSolid(m, Math.floor(x), Math.floor(y));
      for (let a = 0; ok && a < 8; a++) {
        const px = x + Math.cos((a * Math.PI) / 4) * r;
        const py = y + Math.sin((a * Math.PI) / 4) * r;
        if (inSafeArea(m, px, py)) ok = false;
      }
      if (ok && !inSafeArea(m, x, y)) return { arena: { x, y, r }, spawn: { x, y } };
    }
    return null;
  }

  private start(k: number, id: WorldBossId): void {
    const w = this.w;
    let def = WORLD_BOSS[id]!;
    this.done.add(k);
    // On a region map the boss lives at its region site (region metres); on the town map, the data row.
    const place = this.placeOf(def);
    if (!place) return;
    const spawn = place.spawn;
    def = { ...def, arena: place.arena };
    const mode = modeNow(w, 'worldBoss');
    const e = this.h.spawnMonster(id, spawn.x, spawn.y);
    const sims = mode === 'npc' ? WB_SIM.start : 0;
    const hp = bossHp(1 + sims * WB_SCALE.helperWeight);
    Object.assign(e, { hp, maxHp: hp, atk: e.atk * WB_PARTY.atkMul });
    const f: Fight = {
      k,
      def,
      bossId: e.id,
      landed: w.time,
      mode,
      parts: new Map(),
      teles: [],
      baseAtk: e.atk,
      phase: 1,
      beatT: 0,
      beatHp: 0,
      fightT: null,
      lastHit: -1,
      enraged: false,
      bigT: 0,
      sampleT: 0,
      idleT: 0,
      resetting: false,
      decisionT: 2,
      buffT: 0,
      sims: 0,
      simSeq: 0,
      over: null,
    };
    this.fight = f;
    this.mech = new BossMech({
      h: this.h,
      def,
      bossId: e.id,
      mode,
      phase: () => f.phase,
      humanIn: () => !this.w.player.dead && this.inArena(f, this.w.player),
      credit: (id) => this.creditMechanic(id),
      strike: (boss, move, teles) => this.strike(f, boss, move, teles),
    });
    this.addSims(f, sims);
    this.send(id, 'start');
    this.h.notice(def.intro);
  }

  private inArena(f: Fight, e: { x: number; y: number }): boolean {
    return Math.hypot(e.x - f.def.arena.x, e.y - f.def.arena.y) <= f.def.arena.r;
  }

  private stepFight(f: Fight, dt: number): void {
    const w = this.w;
    const e = w.entities.get(f.bossId);
    if (f.over) {
      f.over.t -= dt;
      if (f.over.t <= 0) {
        if (e) w.entities.delete(f.bossId);
        this.fight = null;
      }
      return;
    }
    if (!e) {
      this.fight = null;
      return;
    }
    if (e.dead) return;
    // It leaves when its time is up.
    if (w.time >= slotStart(f.k) + WB_CYCLE.life) {
      this.end(f, false);
      return;
    }
    const p = w.player;
    const humanIn = !p.dead && this.inArena(f, p);
    if (humanIn) {
      const part = this.human(f, p.id)!;
      part.present += dt;
    }
    // Leash: nobody inside for a while sends it home.
    f.idleT = humanIn ? 0 : f.idleT + dt;
    if (!f.resetting && f.idleT > WB_LEASH.idle) {
      f.resetting = true;
      this.cancelCast(f, e, true);
      this.mech?.onPhaseBeat(e);
      this.send(f.def.id, 'leash', 1);
    } else if (f.resetting && humanIn) {
      f.resetting = false;
      if (f.phase > 1) this.mech?.onPhaseStart(e, f.phase as 2 | 3);
      this.send(f.def.id, 'leash', 0);
    }
    if (f.resetting) {
      const ax = f.def.arena.x;
      const ay = f.def.arena.y;
      const d = Math.hypot(ax - e.x, ay - e.y);
      if (d > 0.3) {
        const s = Math.min(d, e.speed * WB_LEASH.homeSpeedMul * dt);
        this.h.move(e, ((ax - e.x) / d) * s, ((ay - e.y) / d) * s);
      }
      e.hp = Math.min(e.maxHp, e.hp + e.maxHp * WB_LEASH.regen * dt);
      return;
    }
    // Presence of the helpers.
    if (f.mode === 'npc' && humanIn)
      for (const q of f.parts.values()) if (q.sim) q.present += dt;
    // A staged beat between phases: no damage, no moves.
    if (f.beatT > 0) {
      f.beatT -= dt;
      e.invulnT = Math.max(e.invulnT, f.beatT);
      e.stunT = Math.max(e.stunT, f.beatT);
      e.cast = null;
      e.hp = Math.max(e.hp, f.beatHp);
      if (f.beatT <= 0) this.mech?.onPhaseStart(e, f.phase as 2 | 3);
      return;
    }
    const share = e.hp / e.maxHp;
    const ph = share <= WB_PHASES.at[1]! ? 3 : share <= WB_PHASES.at[0]! ? 2 : 1;
    if (ph > f.phase) {
      f.phase = ph as 2 | 3;
      f.beatT = WB_PHASES.beat;
      f.beatHp = e.hp;
      this.cancelCast(f, e, false);
      this.mech?.onPhaseBeat(e);
      if (f.mode === 'npc') this.addSims(f, WB_SIM.perPhase);
      this.send(f.def.id, 'phase', ph);
      this.h.notice(f.def.beats[ph - 2]!);
      return;
    }
    // Attendance and health growth, every 10 s: it only goes up.
    f.sampleT += dt;
    if (f.sampleT >= WB_SCALE.sample) {
      f.sampleT = 0;
      const target = bossHp(this.attendance(f));
      if (target > e.maxHp) {
        const ratio = target / e.maxHp;
        e.hp = Math.round(e.hp * ratio);
        e.maxHp = target;
      }
    }
    // The helpers fight (never below the floor: the player finishes it).
    if (f.mode === 'npc' && f.sims && humanIn && w.time - f.landed >= WB_SIM.walkIn) {
      const allowed = Math.max(0, e.hp - WB_SIM.floor * e.maxHp);
      const d = Math.min(f.sims * WB_SIM.dps * dt, allowed);
      if (d > 0) {
        e.hp -= d;
        for (const q of f.parts.values()) if (q.sim) q.dmg += d / f.sims;
        f.fightT ??= 0;
      }
    }
    // ความคลั่ง.
    if (f.fightT != null) {
      f.fightT += dt;
      if (!f.enraged && f.fightT >= WB_ENRAGE.at) {
        f.enraged = true;
        e.atk = f.baseAtk * WB_ENRAGE.dmgMul * (f.buffT > 0 ? 1.3 : 1);
        this.send(f.def.id, 'enrage');
        this.h.notice(`${f.def.name}คลั่ง!`);
      }
    }
    if (f.buffT > 0 && (f.buffT -= dt) <= 0) e.atk = f.baseAtk * (f.enraged ? WB_ENRAGE.dmgMul : 1);
    // The arena is its leash.
    const out = Math.hypot(e.x - f.def.arena.x, e.y - f.def.arena.y) - f.def.arena.r;
    if (out > 0) {
      const d = out + 0.5;
      const l = Math.hypot(f.def.arena.x - e.x, f.def.arena.y - e.y) || 1;
      this.h.move(e, ((f.def.arena.x - e.x) / l) * d, ((f.def.arena.y - e.y) / l) * d);
    }
    // Decide.
    if (f.enraged && f.phase === 3) f.bigT += dt;
    this.mech?.step(dt, e);
    f.decisionT -= dt;
    if (f.decisionT <= 0 && !e.cast && e.stunT <= 0 && humanIn && !this.mech?.busy()) {
      f.decisionT = DECISION;
      this.decide(f, e);
    }
  }

  private cancelCast(f: Fight, e: Entity, calm: boolean): void {
    e.cast = null;
    for (const t of f.teles) this.h.emit({ type: 'dungeonTeleEnd', id: t.id, cancelled: true });
    f.teles.length = 0;
    for (const id of this.mech?.onCancel() ?? []) this.h.emit({ type: 'dungeonTeleEnd', id, cancelled: true });
    if (calm) e.stunT = 0;
  }

  // ───────────────────────── moves ─────────────────────────

  private decide(f: Fight, e: Entity): void {
    const w = this.w;
    const foe = w.player;
    const d = dist(e, foe);
    const ready = (m: BossMove) => !e.cooldowns[m.id];
    const ofPhase = f.def.moves.filter(
      (m) => m.phases.includes(f.phase) && d >= m.min && d <= m.max && (this.mech?.canPick(m.id) ?? true),
    );
    const forced = f.enraged && f.phase === 3 && f.bigT >= WB_ENRAGE.bigEvery ? ofPhase.find((m) => m.big) : undefined;
    let pick: BossMove | undefined = forced;
    if (forced) {
      f.bigT = 0;
      e.cooldowns[forced.id] = 0;
    } else {
      const cands = ofPhase.filter(ready);
      const total = cands.reduce((a, m) => a + m.weight, 0);
      if (!total) return;
      let r = this.h.random() * total;
      for (const m of cands) if ((r -= m.weight) < 0) { pick = m; break; }
      pick ??= cands[cands.length - 1];
    }
    if (pick) w.useSkill(e, pick.id, foe);
  }

  monsterDecide(m: Entity): boolean {
    // The fight decides for its bosses (step), never the World's triangle AI; eggs and rods never act.
    return !!this.fight && (m.id === this.fight.bossId || BossMech.inert(m.brain?.defId));
  }

  /** The farthest living player from the boss inside the arena (a leap's mark). */
  private farthest(f: Fight, e: Entity): Entity | null {
    const p = this.w.player;
    return !p.dead && this.inArena(f, p) ? p : (e.cast && this.w.entities.get(e.cast.targetId ?? -1)) || null;
  }

  private shapesOf(f: Fight, e: Entity, rule: BossMoveRule, tgt: Entity | undefined): BossShape[] {
    const centre = rule.at === 'target' ? tgt : rule.at === 'far' ? (this.farthest(f, e) ?? tgt) : undefined;
    const base = shapeOf(rule, e, tgt, centre);
    if (!base) return [];
    if (rule.spots && base.k === 'ring') {
      const out: BossShape[] = [base];
      const a = f.def.arena;
      for (let i = 1; i < rule.spots; i++) {
        const ang = this.h.random() * Math.PI * 2;
        const rr = Math.sqrt(this.h.random()) * a.r;
        out.push({ k: 'ring', x: a.x + Math.cos(ang) * rr, y: a.y + Math.sin(ang) * rr, r: base.r });
      }
      return out;
    }
    if (rule.parallel && base.k === 'line') {
      const { count, gap } = rule.parallel;
      return Array.from({ length: count }, (_, i) => {
        const off = (i - (count - 1) / 2) * gap;
        return { ...base, x: base.x - base.dy * off, y: base.y + base.dx * off };
      });
    }
    return [base];
  }

  private castStart(f: Fight, e: Entity, move: string): void {
    const mv = WB_MOVES[move];
    if (!mv || !e.cast) return;
    if (f.enraged) e.cast.windup = Math.max(WB_ENRAGE.minWindup, e.cast.windup * WB_ENRAGE.windupMul);
    const rule = mv.rule;
    if (!rule) return;
    // The helpers answer a mechanic move that pays credit.
    if (rule.credit && f.mode === 'npc')
      for (const q of f.parts.values()) if (q.sim && this.h.random() < WB_SIM.mechanic) q.mech++;
    this.mech?.onCast(e, move);
    if (rule.scripted === 'enrage') return;
    const tgt = this.w.entities.get(e.cast.targetId ?? -1);
    const shapes = this.shapesOf(f, e, rule, tgt);
    // Rings that follow one another are bands: each disc fills over its own, later, wind-up.
    const bands = shapes[0] ? this.mech?.bands(move, shapes[0]) : null;
    const teles: Tele[] = bands ?? shapes.map((shape) => ({ id: 0, shape }));
    for (const t of teles) {
      const tele: Tele = { ...t, id: this.teleIds++ };
      f.teles.push(tele);
      this.h.emit({ type: 'dungeonTele', id: tele.id, srcId: e.id, move, shape: tele.shape, seconds: e.cast.windup + (tele.delay ?? 0) });
    }
  }

  private resolve(f: Fight, e: Entity, move: string): void {
    const mv = WB_MOVES[move];
    const rule = mv?.rule;
    if (!mv || !rule || e.dead) return;
    const teles = f.teles.splice(0);
    const now = teles.filter((t) => !t.delay);
    for (const t of now) this.h.emit({ type: 'dungeonTeleEnd', id: t.id, cancelled: false });
    this.mech?.defer(move, teles.filter((t) => t.delay));
    this.mech?.script(e, move);
    if (rule.buff) {
      f.buffT = rule.buff.seconds;
      e.atk = f.baseAtk * (f.enraged ? WB_ENRAGE.dmgMul : 1) * rule.buff.atkMul;
      this.h.notice(`${f.def.name}ตบอกคำราม พลังโจมตีเพิ่มขึ้น!`);
      return;
    }
    if (rule.scripted === 'enrage') return;
    this.strike(f, e, move, now);
    this.mech?.after(e, move, now);
  }

  /** Strikes everyone standing in these telegraphs (the ordinary way, or a band that was held back). */
  private strike(_f: Fight, e: Entity, move: string, teles: Tele[]): void {
    const rule = WB_MOVES[move]?.rule;
    if (!rule || e.dead) return;
    const w = this.w;
    const s = getSkill(move);
    // A held band ends when it lands.
    for (const t of teles) if (t.delay) this.h.emit({ type: 'dungeonTeleEnd', id: t.id, cancelled: false });
    // Bolts near a standing rod strike the rod, not the player.
    const live = teles.filter((t) => !this.mech?.drawBolt(move, t.shape));
    const kind = s.kind === 'heal' || s.kind === 'buff' ? 'power' : s.kind;
    const push = this.mech?.pushDir(move) ?? null;
    const struck = new Set<number>();
    for (const v of [...w.entities.values()]) {
      if (v.dead || v === e || !w.isHostile(e, v)) continue;
      const hitBy = live.find((t) => inShape(t.shape, v) && !(t.inner && Math.hypot(v.x - (t.shape as { x: number }).x, v.y - (t.shape as { y: number }).y) < t.inner));
      if (!hitBy || struck.has(v.id)) continue;
      struck.add(v.id);
      const air = v.z > MOVEMENT.airDodgeHeight;
      if (air !== !!rule.airOnly && (air || rule.airOnly)) {
        if (air)
          this.h.emit({ type: 'hit', srcId: e.id, tgtId: v.id, skillId: move, outcome: 'dodge', damage: 0, crit: false });
        continue;
      }
      const sh = this.mech?.shield(v, e, move) ?? { skip: false, pm: 1, sm: 1 };
      if (sh.skip) continue;
      const blocking = v.blocking;
      const stun = rule.stun ? rule.stun * sh.sm : undefined;
      const out = this.h.hit(e, v, s.power * sh.pm, kind, move, stun);
      const landed = out === 'hit' || out === 'guardBreak' || out === 'interrupt';
      if (!landed || v.dead) continue;
      if (stun && !(blocking && rule.guardStopsStun)) {
        v.stunT = Math.max(v.stunT, stun * (100 / (100 + v.toughness)));
        v.cast = null;
      }
      if (rule.status) w.addStatus(v, rule.status.id, rule.status.stacks ?? 1, e.id);
      const kb = rule.knockback ?? 0;
      if (kb) this.knock(v, e, kb, push);
    }
  }

  private knock(v: Entity, from: Entity, d: number, dir: { dx: number; dy: number } | null = null): void {
    const away = d > 0 ? 1 : -1;
    let dx = dir ? dir.dx * away : (v.x - from.x) * away;
    let dy = dir ? dir.dy * away : (v.y - from.y) * away;
    const l = Math.hypot(dx, dy) || 1;
    [dx, dy] = [dx / l, dy / l];
    for (let i = 0; i < Math.abs(d) / 0.25; i++) if (!this.h.move(v, dx * 0.25, dy * 0.25)) break;
  }

  // ───────────────────────── events ─────────────────────────

  observe(ev: WorldEvent): void {
    const f = this.fight;
    if (!f || f.over) return;
    const w = this.w;
    switch (ev.type) {
      case 'castStart': {
        if (ev.srcId !== f.bossId) return;
        const e = w.entities.get(ev.srcId);
        if (e) this.castStart(f, e, ev.skillId);
        return;
      }
      case 'skill': {
        if (ev.srcId !== f.bossId) return;
        const e = w.entities.get(ev.srcId);
        if (e) this.resolve(f, e, ev.skillId);
        return;
      }
      case 'hit':
        return this.onHit(f, ev.srcId, ev.tgtId, ev.skillId, ev.outcome, ev.damage);
      case 'death':
        if (ev.id === f.bossId) this.end(f, true);
        return;
    }
  }

  private onHit(f: Fight, srcId: number, tgtId: number, skillId: string, outcome: string, damage: number): void {
    const w = this.w;
    const boss = w.entities.get(f.bossId);
    if (!boss) return;
    this.mech?.onAnyHit(srcId, tgtId);
    if (tgtId === f.bossId) {
      const p = this.human(f, srcId);
      if (!p) return;
      p.lastAct = w.time;
      // The mechanics decide how much of the blow counts (ledge, stance, aloft, shell).
      const keep = this.mech?.onBossHit(boss, w.entities.get(srcId), skillId, outcome, damage) ?? 1;
      if (damage > 0) {
        p.dmg += damage * Math.min(1, keep);
        f.fightT ??= 0;
        f.lastHit = srcId;
      }
      // A break (破) landing on a wind-up that can be cancelled cuts it short.
      const rule = boss.cast ? WB_MOVES[boss.cast.skillId]?.rule : undefined;
      const sk = getSkill(skillId);
      if (rule?.breakCancels && sk.kind === 'break' && (outcome === 'hit' || outcome === 'guardBreak' || outcome === 'interrupt')) {
        const move = boss.cast!.skillId;
        this.cancelCast(f, boss, false);
        boss.stunT = Math.max(boss.stunT, 1.5);
        this.h.emit({ type: 'dungeonCancel', id: boss.id, move });
        p.mech++;
      }
      return;
    }
    if (srcId === f.bossId && outcome === 'blocked') {
      const p = this.human(f, tgtId);
      if (!p) return;
      p.lastAct = w.time;
      const nominal = damage > 0 ? damage : Math.round(boss.atk * getSkill(skillId).power * 0.8);
      p.guard += nominal;
    }
  }

  // ───────────────────────── the end ─────────────────────────

  private end(f: Fight, win: boolean): void {
    if (f.over) return;
    const w = this.w;
    const e = w.entities.get(f.bossId);
    this.cancelCast(f, e ?? ({ cast: null, stunT: 0 } as Entity), false);
    for (const id of this.mech?.clear() ?? []) this.h.emit({ type: 'dungeonTeleEnd', id, cancelled: true });
    f.over = { win, t: win ? 8 : 0 };
    if (win) this.pay(f);
    else {
      this.h.notice(`${f.def.name}จากไปแล้ว`);
      if (e) w.entities.delete(f.bossId);
    }
    this.send(f.def.id, 'end', 0, win);
  }

  private pay(f: Fight): void {
    const w = this.w;
    const e = w.entities.get(f.bossId);
    const maxHp = e?.maxHp ?? 1;
    const shares = splitRewards([...f.parts.values()].filter((p) => !p.sim), maxHp, f.lastHit);
    const me = shares.findIndex((x) => x.id === w.player.id);
    const mine = f.parts.get(w.player.id);
    const pts = mine ? contribution({ ...mine, maxHp }) : 0;
    if (me < 0) {
      this.h.emit({ type: 'worldBossLoot', id: f.def.id, tier: 'none', rank: -1, of: shares.length, points: Math.round(pts), full: false, gold: 0, cultivation: 0, items: [], rare: [] });
      this.h.notice(`แต้มสมทบไม่ถึง ${WB_CONTRIB.min} จึงไม่ได้รับรางวัล`);
      return;
    }
    const sh = shares[me]!;
    const pr = w.progress;
    const save = (pr.worldBoss ??= newWorldBossSave());
    if (save.day !== pr.arena.day) Object.assign(save, { day: pr.arena.day, claims: {} });
    const claimed = save.claims[f.def.id] ?? 0;
    const full = claimed < WB_DAILY.fullChests || sh.lastHit;
    save.kills++;
    const gold = full ? sh.gold : WB_DAILY.gold;
    const cultivation = full ? sh.cultivation : Math.round(sh.cultivation * WB_DAILY.cultivationShare);
    pr.gold += gold;
    pr.cultivation += cultivation;
    const items: string[] = [];
    const rare: string[] = [];
    const give = (id: string) => {
      if (!ITEMS[id]) return;
      if (pr.inventory.length >= BAG_SIZE) return this.h.notice('กระเป๋าเต็ม ของบางชิ้นหายไป');
      pr.inventory.push(id);
      items.push(id);
    };
    if (full) {
      if (!sh.lastHit) save.claims[f.def.id] = claimed + 1;
      for (let i = 0; i < sh.materials; i++) give(f.def.materials[i % f.def.materials.length]!);
      for (const r of f.def.rare)
        if (this.h.random() < r.chance * sh.rareMul) {
          rare.push(r.item);
          give(r.item);
        }
    }
    this.h.emit({ type: 'worldBossLoot', id: f.def.id, tier: sh.tier, rank: me, of: shares.length, points: Math.round(pts), full, gold, cultivation, items, rare });
    this.h.emit({ type: 'progressChanged' });
    this.h.log('sys', `ปราบ${f.def.name}${sh.lastHit ? ' (ตีครั้งสุดท้าย)' : ''}: หีบ${{ gold: 'ทอง', silver: 'เงิน', copper: 'ทองแดง' }[sh.tier]} ${gold} ตำลึง แต้มฝึกปรือ ${cultivation}${full ? '' : ' (หีบเต็มวันนี้รับครบแล้ว)'}`);
  }
}

/** The world-boss system of a World (tests, the client's HUD). */
export const worldBossOf = (w: object): WorldBossesSystem | null => SYSTEMS.get(w) ?? null;
